import { computeDamageFromRoll } from "./combat";
import { displayHpToInternal } from "./stacks";
import type { TileKind, Unit, UnitKind } from "./types";
import { MAX_HP, UNIT_DEFS, canUnitDamage, effectiveAttackKind, isIndirectFire } from "./units";

export interface CodexMatchupForecast {
  canAttack: boolean;
  attackDamage: number;
  attackGoldValue: number;
  defenderKilled: boolean;
  counterDamage: number | null;
  counterGoldValue: number | null;
  goldTrade: number;
}

const NO_ATTACK_FORECAST: CodexMatchupForecast = {
  canAttack: false,
  attackDamage: 0,
  attackGoldValue: 0,
  defenderKilled: false,
  counterDamage: null,
  counterGoldValue: null,
  goldTrade: 0,
};

function goldValueFromDamage({
  damage,
  unitHp,
  unitCost,
}: {
  damage: number;
  unitHp: number;
  unitCost: number;
}): number {
  return (Math.min(damage, unitHp) / MAX_HP) * unitCost;
}

function codexAttackerCargo({
  kind,
  attackerHp,
}: {
  kind: UnitKind;
  attackerHp: number;
}): Unit["cargo"] {
  return kind === "landTransport" ? { kind: "rifleman", hp: attackerHp } : undefined;
}

function codexMatchupDamage({
  attackerKind,
  attackerHp,
  attackerCargo,
  defenderKind,
  defenderTileKind,
}: {
  attackerKind: UnitKind;
  attackerHp: number;
  attackerCargo?: Unit["cargo"];
  defenderKind: UnitKind;
  defenderTileKind: TileKind;
}): number {
  return computeDamageFromRoll({
    attacker: { kind: attackerKind, hp: attackerHp, cargo: attackerCargo } as Unit,
    defender: { kind: defenderKind, hp: MAX_HP } as Unit,
    defenderTile: { kind: defenderTileKind },
  });
}

function forecastCodexCounterDamage({
  defenderKind,
  defenderHp,
  attackDamage,
  effectiveAttackerKind,
  attackerTileKind,
}: {
  defenderKind: UnitKind;
  defenderHp: number;
  attackDamage: number;
  effectiveAttackerKind: UnitKind;
  attackerTileKind: TileKind;
}): number | null {
  const bothDirectFire = !isIndirectFire(effectiveAttackerKind) && !isIndirectFire(defenderKind);
  if (
    attackDamage >= defenderHp ||
    !bothDirectFire ||
    !canUnitDamage({ unit: { kind: defenderKind }, defenderKind: effectiveAttackerKind })
  ) {
    return null;
  }

  return codexMatchupDamage({
    attackerKind: defenderKind,
    attackerHp: defenderHp - attackDamage,
    defenderKind: effectiveAttackerKind,
    defenderTileKind: attackerTileKind,
  });
}

export function forecastCodexMatchup({
  attackerKind,
  attackerDisplayHp,
  defenderKind,
  defenderDisplayHp,
  defenderTileKind,
  attackerTileKind,
}: {
  attackerKind: UnitKind;
  attackerDisplayHp: number;
  defenderKind: UnitKind;
  defenderDisplayHp: number;
  defenderTileKind: TileKind;
  attackerTileKind: TileKind;
}): CodexMatchupForecast {
  const attackerHp = displayHpToInternal(attackerDisplayHp);
  const defenderHp = displayHpToInternal(defenderDisplayHp);
  const attackerCargo = codexAttackerCargo({ kind: attackerKind, attackerHp });
  const attackerForDamage = { kind: attackerKind, cargo: attackerCargo };

  if (!canUnitDamage({ unit: attackerForDamage, defenderKind })) {
    return NO_ATTACK_FORECAST;
  }

  return buildCodexMatchupForecast({
    attackerKind,
    attackerHp,
    attackerCargo,
    defenderKind,
    defenderHp,
    defenderTileKind,
    attackerTileKind,
    effectiveAttackerKind: effectiveAttackKind(attackerForDamage),
  });
}

function codexAttackOutcome({
  attackerKind,
  attackerHp,
  attackerCargo,
  defenderKind,
  defenderHp,
  defenderTileKind,
}: {
  attackerKind: UnitKind;
  attackerHp: number;
  attackerCargo?: Unit["cargo"];
  defenderKind: UnitKind;
  defenderHp: number;
  defenderTileKind: TileKind;
}): { attackDamage: number; attackGoldValue: number; defenderKilled: boolean } {
  const attackDamage = codexMatchupDamage({
    attackerKind,
    attackerHp,
    attackerCargo,
    defenderKind,
    defenderTileKind,
  });
  return {
    attackDamage,
    attackGoldValue: goldValueFromDamage({
      damage: attackDamage,
      unitHp: defenderHp,
      unitCost: UNIT_DEFS[defenderKind].cost,
    }),
    defenderKilled: attackDamage >= defenderHp,
  };
}

function codexCounterOutcome({
  defenderKind,
  defenderHp,
  attackDamage,
  effectiveAttackerKind,
  attackerHp,
  attackerTileKind,
}: {
  defenderKind: UnitKind;
  defenderHp: number;
  attackDamage: number;
  effectiveAttackerKind: UnitKind;
  attackerHp: number;
  attackerTileKind: TileKind;
}): { counterDamage: number | null; counterGoldValue: number | null } {
  const counterDamage = forecastCodexCounterDamage({
    defenderKind,
    defenderHp,
    attackDamage,
    effectiveAttackerKind,
    attackerTileKind,
  });
  if (counterDamage === null) {
    return { counterDamage: null, counterGoldValue: null };
  }
  return {
    counterDamage,
    counterGoldValue: goldValueFromDamage({
      damage: counterDamage,
      unitHp: attackerHp,
      unitCost: UNIT_DEFS[effectiveAttackerKind].cost,
    }),
  };
}

function buildCodexMatchupForecast({
  attackerKind,
  attackerHp,
  attackerCargo,
  defenderKind,
  defenderHp,
  defenderTileKind,
  attackerTileKind,
  effectiveAttackerKind,
}: {
  attackerKind: UnitKind;
  attackerHp: number;
  attackerCargo?: Unit["cargo"];
  defenderKind: UnitKind;
  defenderHp: number;
  defenderTileKind: TileKind;
  attackerTileKind: TileKind;
  effectiveAttackerKind: UnitKind;
}): CodexMatchupForecast {
  const { attackDamage, attackGoldValue, defenderKilled } = codexAttackOutcome({
    attackerKind,
    attackerHp,
    attackerCargo,
    defenderKind,
    defenderHp,
    defenderTileKind,
  });
  const { counterDamage, counterGoldValue } = codexCounterOutcome({
    defenderKind,
    defenderHp,
    attackDamage,
    effectiveAttackerKind,
    attackerHp,
    attackerTileKind,
  });

  return {
    canAttack: true,
    attackDamage,
    attackGoldValue,
    defenderKilled,
    counterDamage,
    counterGoldValue,
    goldTrade: attackGoldValue - (counterGoldValue ?? 0),
  };
}
